/** Client-side geodesic helpers — kept in sync with apps/api/src/lib/geo.ts */

const R = 6371000;

export function haversineMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export type LatLng = { lat: number; lng: number };

/** Distanz-basiert entlang der Route — identisch zum Server tick (interpolateRoute). */
export function interpolateRoute(points: LatLng[], t: number): LatLng {
  if (points.length === 0) return { lat: 0, lng: 0 };
  if (points.length === 1 || t <= 0) return points[0];
  if (t >= 1) return points[points.length - 1];

  const segLens: number[] = [];
  let total = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const d = haversineMeters(points[i], points[i + 1]);
    segLens.push(d);
    total += d;
  }
  if (total <= 0) return points[points.length - 1];

  let remain = t * total;
  for (let i = 0; i < segLens.length; i++) {
    const d = segLens[i];
    if (remain <= d || i === segLens.length - 1) {
      const local = d <= 0 ? 1 : Math.min(1, remain / d);
      const a = points[i];
      const b = points[i + 1];
      return {
        lat: a.lat + (b.lat - a.lat) * local,
        lng: a.lng + (b.lng - a.lng) * local,
      };
    }
    remain -= d;
  }
  return points[points.length - 1];
}

/**
 * Parkplatz-Raster relativ zum Wachen-Marker (Bildschirm-Pixel, Anker bleibt lat/lng).
 * Nord-ausgerichtete Reihen: Spalten Ost–West, weitere Reihen südlich darunter.
 * FUTURE: spieler-gezeichnete Stellplätze / Gebäude-Heading — siehe FUTURE_CONTENT.md
 */
export function parkedLotOffset(
  index: number,
  count: number,
  slotW = 26,
  slotH = 30,
): { x: number; y: number } {
  const n = Math.max(1, count);
  const idx = Math.max(0, Math.min(index, n - 1));
  const cols = Math.min(6, Math.max(1, n));
  const col = idx % cols;
  const row = Math.floor(idx / cols);
  const x = (col - (cols - 1) / 2) * slotW;
  // Leicht unter dem Gebäude = „Hof“ südlich des Markers
  const y = 20 + row * slotH;
  return { x, y };
}

/** @deprecated Alias — Kreisstreuung durch Raster ersetzt. */
export function parkedScreenOffset(
  index: number,
  count: number,
  _radiusPx = 16,
): { x: number; y: number } {
  return parkedLotOffset(index, count);
}
